/**
 * The account-setup gate's state machine: one controller per authenticated session. `start()`
 * resolves the re-key signal — from ID-token claims on web, or from one silent userInfo probe
 * in-app, where only an access token is bridged — and either settles (`ready` / `blocked`) or
 * polls userInfo inside a 150 s window until the marker turns terminal, the window expires
 * (`still_waiting`), or the controller is disposed. Probe errors during the wait are tolerated:
 * a flaky network must never park a user the backend would have completed. All timing,
 * randomness and I/O are injected, so the machine is deterministic under test.
 */

import { decodeJwtPayload } from '@utils/jwtUtils'
import { rekeySignalFromClaims, RekeySignal } from './rekeySignal'
import { RekeyProbeError, IRekeyProbeResult } from './userInfoProbe'

export type AccountSetupPhase = 'checking' | 'ready' | 'waiting' | 'still_waiting' | 'blocked'

export interface IAccountSetupDeps {
    /** The web OAuth session's raw ID token, or null when there is none (in-app, IIB, no session). */
    getIdToken (): string | null
    /** True only when a native shell can answer token requests (the in-app WebView). */
    canProbeWithoutClaims (): boolean
    /** A currently valid access token; the owning layer refreshes an expired one. */
    getAccessToken (): Promise<string>
    /**
     * Force a fresh token issuance now (AuthService.forceRefreshAccessToken in production; a
     * no-op 'unsupported' on the native path). Each issuance re-runs the pre-token trigger,
     * which re-enqueues a stuck user — the only way a failed run progresses, since the worker
     * writes no marker on failure. Fire-and-forget; capped at once per window.
     */
    forceTokenRefresh (): Promise<unknown>
    /** Absolute /oauth2/userInfo URL for this session; {@link resolveUserInfoEndpoint} in production. */
    resolveUserInfoEndpoint (accessToken: string): Promise<string>
    /** One live marker read; {@link fetchRekeyMarker} in production. */
    probe (userInfoEndpoint: string, accessToken: string): Promise<IRekeyProbeResult>
    emitMetric (metricName: string, metricValue: number): void
    setTimeoutFn (callback: () => void, delayMs: number): unknown
    clearTimeoutFn (handle: unknown): void
    now (): number
    /** Uniform in [0, 1); injectable so jitter is assertable. */
    random (): number
}

export const ACCOUNT_SETUP_WINDOW_MS = 150000
const FAST_POLL_DELAY_MS = 2000
const FAST_POLL_COUNT = 5
const SLOW_POLL_DELAY_MS = 5000
const JITTER_FRACTION = 0.2
const INITIAL_CHECK_ATTEMPTS = 2
const INITIAL_CHECK_RETRY_DELAY_MS = 2000
// The in-app silent check holds a blank screen, so the whole check — token, discovery, probe,
// retry — is raced against one deadline that fails open; the per-call 10 s deadlines remain
// inner bounds. 8 s matches the release-refetch deadline.
export const INITIAL_CHECK_DEADLINE_MS = 8000
// On consecutive 429/5xx answers the delay doubles from the base (jitter kept), capped here, so
// a throttled or erroring userInfo is not hammered at the normal cadence.
const SERVER_ERROR_BACKOFF_CAP_MS = 20000

/**
 * Immediate first probe (it alone dismisses a merely-stale token), then 2 s × 5, then 5 s —
 * each ±20 % so a migration wave's logins do not probe in lockstep.
 */
export function pollDelayMs (pollIndex: number, random: () => number): number {
    if (pollIndex === 0) {
        return 0
    }
    const base = pollIndex <= FAST_POLL_COUNT ? FAST_POLL_DELAY_MS : SLOW_POLL_DELAY_MS
    return Math.round(base * (1 + JITTER_FRACTION * (2 * random() - 1)))
}

export class AccountSetupController {
    private readonly deps: IAccountSetupDeps
    private readonly windowMs: number
    private readonly listeners = new Set<(phase: AccountSetupPhase) => void>()
    private phase: AccountSetupPhase = 'checking'
    private timer: unknown = null
    private disposed = false
    private pollIndex = 0
    private windowStartedAt = 0
    private shownAt: number | null = null
    private releaseDelay: (() => void) | null = null
    private started = false
    private windowRefreshUsed = false
    private serverErrorStreak = 0
    private windowReads = 0
    private initialCheckTimer: unknown = null
    private releaseInitialCheck: (() => void) | null = null

    constructor (deps: IAccountSetupDeps, windowMs: number = ACCOUNT_SETUP_WINDOW_MS) {
        this.deps = deps
        this.windowMs = windowMs
    }

    public getPhase (): AccountSetupPhase {
        return this.phase
    }

    /** Registers a phase listener and immediately reports the current phase. */
    public subscribe (listener: (phase: AccountSetupPhase) => void): () => void {
        this.listeners.add(listener)
        listener(this.phase)
        return () => this.listeners.delete(listener)
    }

    public async start (): Promise<void> {
        if (this.started) {
            // One controller runs one session; a second start would race two poll loops over one
            // timer handle.
            console.warn('[AccountSetup] start() called twice; ignoring')
            return
        }
        this.started = true
        let idToken: string | null
        try {
            idToken = this.deps.getIdToken()
        } catch (error) {
            // No readable session state: fail open, like an undecodable token.
            console.warn('[AccountSetup] Failed to read session state; failing open', error)
            idToken = null
        }
        if (this.disposed) {
            // dispose() can race start(); staying silently on 'checking' would be undebuggable.
            console.warn('[AccountSetup] start() after dispose; gate will stay on checking')
            return
        }
        if (idToken) {
            this.settleFromSignal(claimsSignal(idToken))
            return
        }
        let canProbe: boolean
        try {
            canProbe = this.deps.canProbeWithoutClaims()
        } catch (error) {
            // Same contract as an unreadable token: an undecidable session fails open. Letting
            // the throw propagate would reject start() and strand the gate on 'checking'.
            console.warn('[AccountSetup] Failed to resolve probe capability; failing open', error)
            this.settle('ready', null)
            return
        }
        if (canProbe) {
            // The whole silent check races one fail-open deadline: its worst-case chain (token
            // bridge, discovery, probe, retry) far exceeds what a blank screen tolerates.
            const outcome = await Promise.race([
                this.runInitialCheck().then(() => 'done' as const),
                new Promise<'deadline' | 'disposed'>((resolve) => {
                    this.releaseInitialCheck = () => resolve('disposed')
                    this.initialCheckTimer = this.deps.setTimeoutFn(() => resolve('deadline'), INITIAL_CHECK_DEADLINE_MS)
                }),
            ])
            this.releaseInitialCheck = null
            if (this.initialCheckTimer != null) {
                this.deps.clearTimeoutFn(this.initialCheckTimer)
                this.initialCheckTimer = null
            }
            if (outcome === 'deadline' && !this.disposed && this.phase === 'checking') {
                this.deps.emitMetric('AccountSetup.InitialCheckFailOpen', 1)
                this.settle('ready', null)
            }
            return
        }
        // Non-Cognito session (IIB, preprod secret-key mode): the re-key never touches these.
        this.settle('ready', null)
    }

    /**
     * From still_waiting: open a fresh window, resume polling, and force one token issuance so
     * the pre-token trigger re-enqueues a user whose run failed without a marker.
     */
    public keepWaiting (): void {
        if (this.disposed || this.phase !== 'still_waiting') {
            return
        }
        this.deps.emitMetric('AccountSetup.KeepWaiting', 1)
        this.beginWindow()
        this.maybeForceTokenRefresh()
    }

    /** Stops all polling. A wait abandoned mid-flight (unmount, sign-out) is recorded as such. */
    public dispose (): void {
        if (this.disposed) {
            return
        }
        this.disposed = true
        this.cancelTimer()
        // Release an initial check parked on its (just-cancelled) retry delay.
        this.releaseDelay?.()
        this.releaseDelay = null
        // Settle start()'s deadline race too: with a hung probe it would otherwise stay pending,
        // its deadline armed, until the deadline fires into a disposed controller.
        this.releaseInitialCheck?.()
        this.releaseInitialCheck = null
        if (this.phase === 'waiting' || this.phase === 'still_waiting') {
            this.emitOutcome('abandoned')
        }
        this.listeners.clear()
    }

    /** Claims arm: the ID token already carries the verdict. */
    private settleFromSignal (signal: RekeySignal): void {
        if (signal === 'PENDING') {
            this.showGate()
            return
        }
        if (signal === 'FAILED') {
            this.settle('blocked', 'failed')
            return
        }
        if (signal === 'UNRECOGNIZED') {
            this.deps.emitMetric('AccountSetup.UnrecognizedMarker', 1)
        }
        this.settle('ready', null)
    }

    /**
     * Silent-check arm (no claims): one probe decides from `checking`; one retry, then fail open
     * under its own counter (Outcome.fail_open is reserved for gates that were shown).
     */
    private async runInitialCheck (): Promise<void> {
        for (let attempt = 1; attempt <= INITIAL_CHECK_ATTEMPTS; attempt += 1) {
            let result: IRekeyProbeResult
            try {
                const accessToken = await this.deps.getAccessToken()
                const endpoint = await this.deps.resolveUserInfoEndpoint(accessToken)
                result = await this.deps.probe(endpoint, accessToken)
            } catch (error) {
                if (this.checkOverruled()) {
                    return
                }
                this.countProbeError(error)
                if (attempt < INITIAL_CHECK_ATTEMPTS) {
                    await this.delay(INITIAL_CHECK_RETRY_DELAY_MS)
                    if (this.checkOverruled()) {
                        return
                    }
                }
                continue
            }
            if (this.checkOverruled()) {
                return
            }
            if (result.marker === 'FAILED') {
                this.settle('blocked', 'failed')
                return
            }
            if (result.marker === 'UNRECOGNIZED') {
                this.deps.emitMetric('AccountSetup.UnrecognizedMarker', 1)
                this.settle('ready', null)
                return
            }
            if (result.marker === 'ABSENT' && result.oldSubPresent) {
                // Mid-migration: no terminal marker yet.
                this.showGate()
                return
            }
            this.settle('ready', null)
            return
        }
        if (this.checkOverruled()) {
            return
        }
        this.deps.emitMetric('AccountSetup.InitialCheckFailOpen', 1)
        this.settle('ready', null)
    }

    /** True when the silent check must stop acting: disposed, or the deadline already settled. */
    private checkOverruled (): boolean {
        return this.disposed || this.phase !== 'checking'
    }

    private showGate (): void {
        this.shownAt = this.deps.now()
        this.deps.emitMetric('AccountSetup.Shown', 1)
        this.beginWindow()
    }

    private beginWindow (): void {
        this.windowStartedAt = this.deps.now()
        this.pollIndex = 0
        this.windowRefreshUsed = false
        this.serverErrorStreak = 0
        this.windowReads = 0
        this.setPhase('waiting')
        this.scheduleNextPoll()
    }

    private scheduleNextPoll (): void {
        let delayMs = pollDelayMs(this.pollIndex, this.deps.random)
        if (this.serverErrorStreak > 0) {
            // Back off a throttled/erroring userInfo: double per consecutive 429/5xx, capped.
            delayMs = Math.min(delayMs * Math.pow(2, this.serverErrorStreak), SERVER_ERROR_BACKOFF_CAP_MS)
        }
        if (this.deps.now() + delayMs - this.windowStartedAt >= this.windowMs) {
            if (this.windowReads === 0) {
                // The whole window passed without one successful read: the outage signature
                // (userInfo down, CORS/CSP broken), as opposed to "workers slow".
                this.deps.emitMetric('AccountSetup.WindowZeroReads', 1)
            }
            this.emitOutcome('timeout')
            this.setPhase('still_waiting')
            // A fresh issuance re-enqueues a user whose run failed without a marker, so the wait
            // the user may choose to extend can actually progress.
            this.maybeForceTokenRefresh()
            return
        }
        this.timer = this.deps.setTimeoutFn(() => {
            void this.pollOnce()
        }, delayMs)
    }

    private async pollOnce (): Promise<void> {
        this.pollIndex += 1
        let result: IRekeyProbeResult
        try {
            const accessToken = await this.deps.getAccessToken()
            const endpoint = await this.deps.resolveUserInfoEndpoint(accessToken)
            result = await this.deps.probe(endpoint, accessToken)
        } catch (error) {
            if (!this.disposed) {
                this.countProbeError(error)
                if (error instanceof RekeyProbeError && error.kind === 'unauthorized') {
                    // The cached token was rejected while locally valid; one forced refresh
                    // per window gets a new one (and re-runs the pre-token trigger).
                    this.maybeForceTokenRefresh()
                }
                // The backoff tracks CONSECUTIVE throttle/server errors: any other answer —
                // network blip, 4xx, token-layer failure — resets it, or a recovered server
                // would stay pinned at the cap behind unrelated noise.
                const serverThrottle = error instanceof RekeyProbeError &&
                    error.kind === 'http' && (error.status === 429 || (error.status ?? 0) >= 500)
                this.serverErrorStreak = serverThrottle ? this.serverErrorStreak + 1 : 0
                this.scheduleNextPoll()
            }
            return
        }
        if (this.disposed) {
            return
        }
        this.serverErrorStreak = 0
        this.windowReads += 1
        if (result.marker === 'ABSENT' && !result.oldSubPresent) {
            // The migration breadcrumbs are gone from the live attributes (operator rollback or
            // cleanup) while the stored ID token still said PENDING: nothing left to wait for.
            this.settle('ready', 'fail_open')
            return
        }
        if (result.marker === 'UNRECOGNIZED') {
            // Booked as fail_open, never as a completion, so the completion funnel stays truthful.
            this.deps.emitMetric('AccountSetup.UnrecognizedMarker', 1)
            this.settle('ready', 'fail_open')
            return
        }
        if (result.marker === 'COMPLETE') {
            this.deps.emitMetric('AccountSetup.PollsToComplete', this.pollIndex)
            this.settle('ready', 'complete')
            return
        }
        if (result.marker === 'FAILED') {
            this.settle('blocked', 'failed')
            return
        }
        this.scheduleNextPoll()
    }

    /** Tolerated but counted by kind: separates "userInfo down" from "workers slow" on a wave day. */
    private countProbeError (error: unknown): void {
        if (error instanceof RekeyProbeError) {
            this.deps.emitMetric(`AccountSetup.ProbeError.${error.kind}`, 1)
        } else {
            // Token-layer and other non-probe failures: counted under their own name (the
            // ProbeError.* family stays scoped to typed probe failures) and warned for logs.
            this.deps.emitMetric('AccountSetup.NonProbeError', 1)
            console.warn('[AccountSetup] poll failed', error)
        }
    }

    /** At most one forced issuance per window; fire-and-forget, failures only warn. */
    private maybeForceTokenRefresh (): void {
        if (this.windowRefreshUsed) {
            return
        }
        this.windowRefreshUsed = true
        void Promise.resolve()
            .then(() => this.deps.forceTokenRefresh())
            .catch((error) => console.warn('[AccountSetup] forced token refresh failed', error))
    }

    private delay (delayMs: number): Promise<void> {
        return new Promise((resolve) => {
            this.releaseDelay = resolve
            this.timer = this.deps.setTimeoutFn(() => {
                this.releaseDelay = null
                resolve()
            }, delayMs)
        })
    }

    private settle (phase: 'ready' | 'blocked', outcome: string | null): void {
        this.cancelTimer()
        // cancelTimer may have just killed a retry delay's timer (the silent check parks on it);
        // release the awaiting check so its promise settles — it re-checks checkOverruled().
        this.releaseDelay?.()
        this.releaseDelay = null
        if (outcome) {
            this.emitOutcome(outcome)
        }
        this.setPhase(phase)
    }

    /**
     * Outcomes are per-episode by design: Outcome.timeout counts each expired window (it is the
     * window-expiry counter), KeepWaiting counts recoveries, and a completion after Keep waiting
     * is a real completion — rates normalize by Shown and KeepWaiting. DwellMillis always
     * measures from the first Shown, so a post-keep-waiting emission reports the total time the
     * user actually waited.
     */
    private emitOutcome (outcome: string): void {
        this.deps.emitMetric(`AccountSetup.Outcome.${outcome}`, 1)
        if (this.shownAt != null) {
            this.deps.emitMetric('AccountSetup.DwellMillis', this.deps.now() - this.shownAt)
        }
    }

    private cancelTimer (): void {
        if (this.timer != null) {
            this.deps.clearTimeoutFn(this.timer)
            this.timer = null
        }
    }

    private setPhase (phase: AccountSetupPhase): void {
        this.phase = phase
        for (const listener of this.listeners) {
            listener(phase)
        }
    }
}

function claimsSignal (idToken: string): RekeySignal {
    const claims = decodeJwtPayload(idToken)
    return claims ? rekeySignalFromClaims(claims) : 'NOT_MIGRATED'
}
